import type { InputOptionType, PageResponse, SubscriptionsCount, SubscriptionViewDto, SystemDto, UserSubscription } from "src/types";
import { NOTIFICATIONS_TAG, SUBSCRIPTIONS_TAG } from "./constants";
import { rootApi } from "./rootApi";
import type { DeleteSubscriptionPayload, GetAllSubscriptionsPayload, UpdateNotificationPayload, UpdateSubscriptionPayload } from "./types";

const subscriptionApi = rootApi.enhanceEndpoints({ addTagTypes: [NOTIFICATIONS_TAG, SUBSCRIPTIONS_TAG] });

const subscriptionEndpoints = subscriptionApi.injectEndpoints({
    endpoints: (build) => ({
        getMySubscriptions: build.query<SubscriptionViewDto, void>({
            query: () => "/subscription/mine",
            providesTags: [SUBSCRIPTIONS_TAG],
        }),
        getSpecificSystems: build.query<InputOptionType[], void>({
            query: () => "/subscription/specific-systems",
            transformResponse: (response: SystemDto[]) =>
                response.map((option) => ({ label: option.jiraSystemName, value: option.jiraSystemId })),
        }),
        updateSubscription: build.mutation<void, UpdateSubscriptionPayload>({
            query: ({ csrfToken, ...body }) => ({
                url: "/subscription",
                method: "POST",
                body,
                headers: {
                    "X-CSRF-TOKEN": csrfToken,
                },
            }),
            invalidatesTags: [SUBSCRIPTIONS_TAG],
        }),
        updateNotification: build.mutation<void, UpdateNotificationPayload>({
            query: ({ csrfToken, ...body }) => ({
                url: "/subscription/notification",
                method: "POST",
                body,
                headers: {
                    "X-CSRF-TOKEN": csrfToken,
                },
            }),
            invalidatesTags: [NOTIFICATIONS_TAG],
        }),
        extendSubscription: build.mutation<void, string>({
            query: (csrfToken) => ({
                url: "/subscription/extend",
                method: "POST",
                headers: {
                    "X-CSRF-TOKEN": csrfToken,
                },
            }),
            invalidatesTags: [SUBSCRIPTIONS_TAG],
        }),
        deleteSubscription: build.mutation<void, DeleteSubscriptionPayload>({
            query: ({ csrfToken, id }) => ({
                url: `/subscription/${id}`,
                method: "DELETE",
                headers: {
                    "X-CSRF-TOKEN": csrfToken,
                },
            }),
            invalidatesTags: [SUBSCRIPTIONS_TAG],
        }),
        getAllSubscriptions: build.query<PageResponse<UserSubscription>, GetAllSubscriptionsPayload>({
            query: ({ page, size, sort, direction, search, csrfToken }) => ({
                url: "/subscription/all",
                method: "GET",
                params: { page, size, sort, direction, search },
                headers: {
                    "X-CSRF-TOKEN": csrfToken,
                },
            }),
            providesTags: [SUBSCRIPTIONS_TAG],
        }),
        getSubscriptionsCount: build.query<SubscriptionsCount, void>({
            query: () => "/subscription/count",
            providesTags: [SUBSCRIPTIONS_TAG],
        }),
    }),
});

export const {
    useGetMySubscriptionsQuery,
    useGetSpecificSystemsQuery,
    useUpdateSubscriptionMutation,
    useUpdateNotificationMutation,
    useExtendSubscriptionMutation,
    useDeleteSubscriptionMutation,
    useGetAllSubscriptionsQuery,
    useGetSubscriptionsCountQuery,
} = subscriptionEndpoints;
